
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { examCandidateAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import DashboardHeader from "@/components/candidate/DashboardHeader";
import CompanyFilter from "@/components/candidate/CompanyFilter";
import StatsCards from "@/components/candidate/StatsCards";
import ExamTabs from "@/components/candidate/ExamTabs";
import ViewResultModal from "@/components/candidate/ViewResultModal";

const CandidateDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [selectedResult, setSelectedResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingResult, setLoadingResult] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState([]);
  const [examType, setExamType] = useState("pending");

  const fetchDashboardData = async (companyFilter = null) => {
    try {
      setIsLoading(true);

      const requestBody = {
        companyId: companyFilter || selectedCompany || "",
      };

      const response = await examCandidateAPI.getDashboard(requestBody);

      setDashboardData(response.data);

      // Update companies list
      if (response.data.companies?.length > 0) {
        setCompanies(response.data.companies);
      }

      // Handle selected company from response
      if (response.data.selectedCompany) {
        setSelectedCompany(response.data.selectedCompany);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast({
        title: "Error",
        description: "Failed to load dashboard data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
    setExamType("pending");
    fetchDashboardData(companyId);
  };

  const handleExamTypeChange = (newExamType) => {
    setExamType(newExamType);
  };

  const handleStartExam = (assignmentId) => {
    navigate(`/candidate/instructions/${assignmentId}`);
  };

  const handleViewResult = async (assignment) => {
    try {
      setLoadingResult(true);

      if (assignment.resultId?._id) {
        const response = await examCandidateAPI.getExamResult(assignment.resultId._id);
        setSelectedResult({
          ...response.data,
          examName: assignment.examId.name,
        });
        setShowResultModal(true);
      } else {
        toast({
          title: "Error",
          description: "Result data not available",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error viewing result:", error);
      toast({
        title: "Error",
        description: "Failed to load result details",
        variant: "destructive",
      });
    } finally {
      setLoadingResult(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData(selectedCompany);
  };

  if (isLoading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Failed to load dashboard data</p>
      </div>
    );
  }

  const { candidate, pendingExams, ongoingExams, completedExams, stats } = dashboardData;

  return (
    <div className="space-y-6">
      <DashboardHeader candidate={candidate} />

      <CompanyFilter
        selectedCompany={selectedCompany}
        onCompanyChange={handleCompanyChange}
        companies={companies}
        onRefresh={handleRefresh}
        isLoading={isLoading}
      />

      {selectedCompany && selectedCompany !== "" ? (
        <>
          <StatsCards stats={stats} />

          <ExamTabs
            pendingExams={pendingExams}
            ongoingExams={ongoingExams}
            completedExams={completedExams}
            selectedCompany={selectedCompany}
            companies={companies}
            onStartExam={handleStartExam}
            onViewResult={handleViewResult}
            loadingResult={loadingResult}
            examType={examType}
            onExamTypeChange={handleExamTypeChange}
          />
        </>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">
            {companies.length > 0
              ? "Please select a company from the dropdown above to view your exam data."
              : "Choose a company to view the statistics"}
          </p>
        </div>
      )}

      <ViewResultModal
        open={showResultModal}
        onOpenChange={setShowResultModal}
        examResult={selectedResult}
        examName={selectedResult?.examName}
      />
    </div>
  );
};

export default CandidateDashboard;
